import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  Box,
  IconButton,
  Button,
  TextField,
  Chip,
  Divider,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import ShareIcon from '@mui/icons-material/Share';
import { formatDistanceToNow } from 'date-fns';

function CommunityPost({ post, onAddComment }) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);

  const handleLike = () => {
    setLiked(!liked);
    post.likes += liked ? -1 : 1;
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      onAddComment(post.id, {
        id: Date.now(),
        author: {
          name: 'Current User',
          avatar: '/avatars/default.jpg',
          role: 'Caregiver'
        },
        content: newComment,
        timestamp: new Date(),
      });
      setNewComment('');
    }
  };

  return (
    <Card sx={{ mb: 3, boxShadow: 2 }}>
      <CardContent>
        {/* Author Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            src={post.author.avatar} 
            alt={post.author.name}
            sx={{ width: 48, height: 48, mr: 2 }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {post.author.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {post.author.role} • {formatDistanceToNow(new Date(post.timestamp), { addSuffix: true })}
            </Typography>
          </Box>
        </Box>

        {/* Post Content */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          {post.content}
        </Typography>

        {/* Tags */}
        {post.tags && (
          <Box sx={{ mb: 2 }}>
            {post.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        {/* Images */}
        {post.images && (
          <Box sx={{ mb: 2 }}>
            {post.images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Post image ${index + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'cover',
                  borderRadius: 8,
                  marginBottom: 8
                }}
              />
            ))}
          </Box>
        )}

        {/* Engagement Stats */}
        <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', mb: 1 }}>
          <Typography variant="body2">{post.likes} likes</Typography>
          <Typography variant="body2">{post.comments.length} comments</Typography>
        </Box>
      </CardContent>

      <Divider />

      {/* Action Buttons */}
      <CardActions sx={{ px: 2 }}>
        <IconButton onClick={handleLike} color={liked ? 'primary' : 'default'}>
          <ThumbUpIcon />
        </IconButton>
        <IconButton onClick={() => setShowComments(!showComments)}>
          <CommentIcon />
        </IconButton>
        <IconButton>
          <ShareIcon />
        </IconButton>
      </CardActions>

      {/* Comments Section */}
      {showComments && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          
          {/* Comment Input */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              multiline
              maxRows={4}
            />
            <Button 
              variant="contained" 
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Post
            </Button>
          </Box>

          {/* Comments List */}
          {post.comments.map((comment) => (
            <Box key={comment.id} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Avatar 
                src={comment.author.avatar} 
                alt={comment.author.name}
                sx={{ width: 32, height: 32 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2">
                    {comment.author.name}
                  </Typography>
                  <Typography variant="body2">
                    {comment.content}
                  </Typography>
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {formatDistanceToNow(new Date(comment.timestamp), { addSuffix: true })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
}

export default CommunityPost;
